import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { broadcastResponseToMainFrame } from '@azure/msal-browser/redirect-bridge';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from './authConfig';
import { setMsalInstance } from './api/client';
import { runRedirectBridgeIfLanding } from './utils/redirect-bridge-guard';
import './index.css';
import App from './App';

const msalInstance = new PublicClientApplication(msalConfig);

// Register the instance with the API client before <App /> mounts.
// api/client.js holds a module-level reference and uses it for the
// acquireTokenSilent / acquireTokenPopup flow on every mgmt/* call.
setMsalInstance(msalInstance);

async function bootstrap() {
  // ── MSAL v5 redirect bridge — MUST run before initialize() ──
  // A silent token renew opens a HIDDEN IFRAME to /authorize?prompt=none;
  // Entra redirects it to our redirectUri (/#code=…&state=…) and the main
  // window waits on a BroadcastChannel for THIS document to post the
  // response back. msal-browser v5 does not do that for us, so when this
  // document is that landing we broadcast and stop — the admin SPA must
  // never boot inside the hidden iframe (initialising MSAL there throws
  // block_iframe_reload). Without this every renew timed out and the
  // portal showed "Something went wrong" with zero API calls after an
  // idle past the refresh token (2026-09-11; see the user SWA's
  // utils/redirect-bridge-guard.js for the full account — this SPA's
  // copy is its verbatim twin, drift-locked by a test).
  if (await runRedirectBridgeIfLanding(window, broadcastResponseToMainFrame)) {
    return;
  }

  await msalInstance.initialize();

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
      msalInstance.setActiveAccount(event.payload.account);
    }
  });

  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </MsalProvider>
    </React.StrictMode>
  );
}

bootstrap();
